/** Shared Dashplan display constants. */
import {
  Users,
  Timer,
  Car,
  Coffee,
  FileText,
  Home,
  Circle,
  type LucideIcon,
} from "lucide-react";

export type DashplanKind =
  | "meeting"
  | "focus"
  | "travel"
  | "break"
  | "admin"
  | "personal"
  | "other";

export type DashplanStatus = "planned" | "in_progress" | "done" | "skipped";

/**
 * Each kind gets an icon and a colour pair. `accent` is a border/text colour,
 * `surface` a translucent fill, so blocks stay legible in both themes without
 * a second palette.
 */
export const KINDS: Record<
  DashplanKind,
  { label: string; icon: LucideIcon; accent: string; surface: string }
> = {
  meeting: {
    label: "Meeting",
    icon: Users,
    accent: "text-sky-400 border-sky-400/60",
    surface: "bg-sky-400/10",
  },
  focus: {
    label: "Focus",
    icon: Timer,
    accent: "text-primary border-primary/60",
    surface: "bg-primary/10",
  },
  travel: {
    label: "Travel",
    icon: Car,
    accent: "text-amber-400 border-amber-400/60",
    surface: "bg-amber-400/10",
  },
  break: {
    label: "Break",
    icon: Coffee,
    accent: "text-teal-400 border-teal-400/60",
    surface: "bg-teal-400/10",
  },
  admin: {
    label: "Admin",
    icon: FileText,
    accent: "text-slate-400 border-slate-400/60",
    surface: "bg-slate-400/10",
  },
  personal: {
    label: "Personal",
    icon: Home,
    accent: "text-violet-400 border-violet-400/60",
    surface: "bg-violet-400/10",
  },
  other: {
    label: "Other",
    icon: Circle,
    accent: "text-muted-foreground border-border",
    surface: "bg-muted/40",
  },
};

export const KIND_ORDER: DashplanKind[] = [
  "meeting",
  "focus",
  "travel",
  "break",
  "admin",
  "personal",
  "other",
];

/**
 * Pixels per minute on the timeline.
 *
 * 1.8 puts a 30-minute block at 54px, which is what the resize handles need:
 * two 12px edge strips and a body still worth tapping. At the previous 1.4 a
 * half-hour block was 42px and could not carry both. The day is ~29% taller
 * for it, on a view that already scrolls.
 */
export const PX_PER_MIN = 1.8;

/** Timeline starts at 06:00 and ends at 23:00 unless blocks fall outside. */
export const DEFAULT_START_HOUR = 6;
export const DEFAULT_END_HOUR = 23;

/** Common durations offered in the block editor. */
export const DURATION_PRESETS = [15, 30, 45, 60, 90, 120, 180, 240];
